"use client";

import Link from "next/link";
import Image from "next/image";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Turnstile } from "@/components/Turnstile";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { validateGroupClassInterest } from "@/lib/groupClassWaitlist";

const fieldClass = "mt-2 w-full rounded-xl border border-[#d8ccb6] bg-white px-4 py-3 text-base text-[#113b35] placeholder:text-[#7a8780] outline-none focus:border-[#a46f12] focus:ring-2 focus:ring-[#d5a746]/30";

export function GroupClassWaitlist({ siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "", sourcePath = "/" }: { siteKey?: string; sourcePath?: "/" | "/group-singing-classes" }) {
  const id = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [verificationActive, setVerificationActive] = useState(false);
  const [token, setToken] = useState("");
  const [verificationKey, setVerificationKey] = useState(0);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [existing, setExisting] = useState(false);
  const [widgetSize, setWidgetSize] = useState<"compact" | "flexible">("compact");
  useEffect(() => {
    const query = window.matchMedia("(min-width: 640px)");
    const update = () => setWidgetSize(query.matches ? "flexible" : "compact");
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const [error, setError] = useState("");
  const track = useTrackEvent();
  const clearToken = useCallback(() => setToken(""), []);

  useEffect(() => {
    if (verificationActive) return;
    const form = formRef.current;
    if (!form) return;
    if (typeof IntersectionObserver === "undefined") {
      setVerificationActive(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVerificationActive(true);
        observer.disconnect();
      }
    }, { rootMargin: "600px 0px" });
    observer.observe(form);
    return () => observer.disconnect();
  }, [verificationActive]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setVerificationActive(true);
    if (status === "loading") return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    const payload = { name: fields.get("name"), email: fields.get("email"), phone: fields.get("phone"), note: fields.get("note"), consent: fields.get("consent") === "on", companyWebsite: fields.get("companyWebsite"), turnstileToken: token, sourcePath };
    const checked = validateGroupClassInterest(payload);
    if ("error" in checked) { setError(checked.error ?? "Please check your details and try again."); setStatus("error"); return; }
    if (!siteKey || !token) { setError("Please complete the human verification. If it is unavailable, try again later."); setStatus("error"); return; }
    setStatus("loading");
    setError("");
    try {
      const response = await fetch("/api/group-class-waitlist", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok || data.ok !== true) throw new Error(data.error || "We could not save your interest. Please try again.");
      form.reset();
      setExisting(data.existing === true);
      setStatus("success");
      if (data.existing !== true) track("group_class_waitlist_signup", { source_page: sourcePath, source_section: "group_classes", funnel: "local_lessons" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "We could not save your interest. Please try again.");
      setStatus("error");
      clearToken();
      setVerificationKey((value) => value + 1);
    }
  }

  return (
    <section id="group-classes" aria-labelledby={`${id}-title`} className="scroll-mt-28 rounded-[28px] border border-[#dfcfb3] bg-[#f6f1e7] p-6 text-[#113b35] shadow-[0_20px_60px_rgba(24,43,37,0.07)] sm:p-8 lg:p-10">
      <div className="grid items-start gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#96610d]">Sing together</p>
          <h2 id={`${id}-title`} className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">Interested in group singing classes?</h2>
          <p className="mt-4 text-base leading-relaxed text-[#586b63]">Join the course waiting list and get details when the next local group classes are announced. Tell us a little about what you would like to work on.</p>
          <ul className="mt-6 space-y-3 text-sm font-semibold">
            <li>✓ Explore technique, confidence and expression</li>
            <li>✓ Be first to hear about dates, location and prices</li>
            <li>✓ Register your interest with no payment or commitment</li>
          </ul>
          <p className="mt-6 text-sm leading-relaxed text-[#586b63]">Dates and availability will be confirmed before booking opens. {sourcePath === "/" ? <><Link href="/group-singing-classes" className="font-semibold text-[#113b35] underline underline-offset-4">Explore group singing classes</Link>. </> : null}Prefer an individual lesson now? <Link href="/local-lessons#buy" className="font-semibold text-[#113b35] underline underline-offset-4">Explore your first lesson</Link>.</p>
          <Image src="/images/local-lessons/singer-warm-stage.webp" alt="A singer at a microphone in warm stage lighting" width={456} height={339} sizes="(min-width: 1024px) 30vw, 90vw" className="mt-7 h-auto w-full max-w-md rounded-2xl" />
        </div>
        {status === "success" ? (
          <div role="status" className="rounded-2xl border border-[#d6c491] bg-white p-7">
            <p className="font-serif text-2xl">{existing ? "Request received." : "You’re on the waiting list."}</p>
            <p className="mt-3 leading-relaxed text-[#586b63]">{existing ? <>Your existing registration and contact preferences remain unchanged. If you need to update your details, <Link href="/contact" className="font-semibold underline underline-offset-4">contact us</Link>.</> : "Thank you for your interest. We’ll contact you when group class details are available. There’s nothing to pay now."}</p>
          </div>
        ) : (
          <form ref={formRef} onFocusCapture={() => setVerificationActive(true)} onSubmit={submit} aria-busy={status === "loading"} className="rounded-2xl border border-[#e6ddce] bg-white/75 p-5 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold sm:col-span-2">Your name <span aria-hidden="true">*</span><input name="name" autoComplete="name" required maxLength={120} className={fieldClass} /></label>
              <label className="text-sm font-semibold">Email address <span aria-hidden="true">*</span><input name="email" type="email" autoComplete="email" required maxLength={254} className={fieldClass} /></label>
              <label className="text-sm font-semibold">Telephone number <span aria-hidden="true">*</span><input name="phone" type="tel" autoComplete="tel" required maxLength={40} aria-describedby={`${id}-phone-hint`} className={fieldClass} /></label>
              <p id={`${id}-phone-hint`} className="-mt-1 text-xs leading-relaxed text-[#586b63] sm:col-span-2">For numbers outside the UK, include your country code.</p>
              <label className="text-sm font-semibold sm:col-span-2">A note for Liuba (optional)<textarea name="note" maxLength={2000} rows={3} placeholder="Your singing goals, preferred studio or availability…" className={`${fieldClass} resize-y`} /></label>
            </div>
            <input name="companyWebsite" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <label className="mt-4 flex items-start gap-3 text-sm leading-relaxed text-[#586b63]"><input name="consent" type="checkbox" required className="mt-1 h-4 w-4 shrink-0 accent-[#113b35]" /><span>I agree to be contacted by email or telephone about group singing classes. Read our <Link href="/privacy" className="font-semibold text-[#113b35] underline underline-offset-4">Privacy Policy</Link>.</span></label>
            <div className="mt-4 min-h-[140px] max-w-full sm:min-h-[65px]">
              {verificationActive ? <Turnstile key={`${verificationKey}-${widgetSize}`} size={widgetSize} siteKey={siteKey} onVerify={setToken} onExpire={clearToken} onError={clearToken} className="max-w-full" /> : null}
            </div>
            {error ? <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800">{error}</p> : null}
            <button disabled={status === "loading"} type="submit" className="mt-5 inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-[#d5a746] px-6 py-3 text-sm font-bold text-[#113b35] transition hover:bg-[#e2b85d] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#113b35] disabled:cursor-wait disabled:opacity-60">{status === "loading" ? "Saving your interest…" : "Join the waiting list & get details"}</button>
            <p className="mt-3 text-center text-xs text-[#586b63]">Required fields marked *. Group class updates only.</p>
          </form>
        )}
      </div>
    </section>
  );
}
